import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  CardActions,
  Divider
} from '@mui/material';
import {
  CloudUpload,
  Security,
  Download,
  CheckCircle,
  Error
} from '@mui/icons-material';
import { fingerprintService } from '../services/fingerprintService';
import { EmbedResult } from '../types';

const EmbedFingerprint: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [clientId, setClientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EmbedResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleEmbed = async () => {
    if (!file || !clientId.trim()) {
      setError('Por favor selecciona un archivo e ingresa un ID de cliente');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const embedResult = await fingerprintService.embedFingerprint(file, clientId);
      setResult(embedResult);
    } catch (error: any) {
      setError(error.message || 'Error al embeber el fingerprint');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (result?.fileUrl) {
      const link = document.createElement('a');
      link.href = result.fileUrl;
      link.download = `fingerprinted_${file?.name || 'file'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getFileType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'dcm':
        return 'DICOM';
      case 'json':
        return 'JSON';
      default:
        return 'Binario';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
        Embeber Fingerprint
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        <Box sx={{ flex: { xs: 1, md: 1 } }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Seleccionar Archivo
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <input
                accept=".dcm,.json,application/octet-stream"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  sx={{ width: '100%', py: 2 }}
                >
                  Seleccionar Archivo
                </Button>
              </label>
            </Box>

            {file && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Archivo seleccionado:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {file.name}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={getFileType(file.name)} 
                      size="small" 
                      color="primary" 
                    />
                    <Chip 
                      label={`${(file.size / 1024 / 1024).toFixed(2)} MB`} 
                      size="small" 
                      variant="outlined" 
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            <TextField
              fullWidth
              label="ID del Cliente"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Ej: cliente123"
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              fullWidth
              onClick={handleEmbed}
              disabled={!file || !clientId.trim() || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Security />}
              sx={{
                py: 1.5,
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a6fd8, #6a4190)'
                }
              }}
            >
              {loading ? 'Procesando...' : 'Embeber Fingerprint'}
            </Button>
          </Paper>
        </Box>

        <Box sx={{ flex: { xs: 1, md: 1 } }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Resultado
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {result && (
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {result.success ? (
                      <CheckCircle color="success" sx={{ mr: 1 }} />
                    ) : (
                      <Error color="error" sx={{ mr: 1 }} />
                    )}
                    <Typography variant="h6" color={result.success ? 'success.main' : 'error.main'}>
                      {result.success ? 'Fingerprint Embeber Exitosamente' : 'Error al Embeber'}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Fingerprint generado:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      backgroundColor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      wordBreak: 'break-all'
                    }}
                  >
                    {result.fingerprint}
                  </Typography>

                  <Typography variant="body2" sx={{ mt: 2 }}>
                    {result.message}
                  </Typography>

                  {result.success && result.fileUrl && (
                    <CardActions sx={{ mt: 2, p: 0 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleDownload}
                        fullWidth
                      >
                        Descargar Archivo con Fingerprint
                      </Button>
                    </CardActions>
                  )}
                </CardContent>
              </Card>
            )}

            {!result && !error && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Security sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Selecciona un archivo y un ID de cliente para comenzar
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default EmbedFingerprint;
